import { FormEvent, useCallback, useState } from 'react';
// eslint-disable-next-line no-restricted-imports
import { DirectMetricsGoalUI } from '../../../data-layer/direct/types';
import { useTranslation } from '../../../utils/useTranslation';
import { Dialog, DialogContent, DialogFooter, DialogTitle } from '../../feedback/Dialog';
import { Button } from '../../input/Button';
import { Input } from '../../input/Input';
import { Box } from '../../layout/Box';
import { DataTestId } from '../../layout/DataTestIdWrapper';
import { Typography } from '../../typography/Typography';
import { FOOTER_HEIGHT, MAX_WIDTH } from './constants';
import { ContentPromotionGoalsDialogContent } from './parts/ContentPromotionGoalsDialogContent/ContentPromotionGoalsDialogContent';
import { ContentPromotionGoalsDialogProps } from './types';
import { isAllSelected, makeIdentifiers, makeSelected, makeSelectedMapping } from './utils';

export const ContentPromotionGoalsDialog: React.FC<ContentPromotionGoalsDialogProps> = ({
    goals,
    selected: selectedBase,
    onClose,
    onSelect,
}) => {
    const t = useTranslation();

    const [selected, setSelected] = useState<Record<string, boolean>>(makeSelected(selectedBase));
    const selectedIds = makeIdentifiers(selected);
    const [foundGoals, setFoundGoals] = useState<DirectMetricsGoalUI[] | undefined>(goals);

    const onSelectRow = useCallback(
        (id: string, value: boolean) => {
            setSelected({ ...selected, [id]: value });
        },
        [selected],
    );

    const onSelectAll = useCallback(() => {
        if (!foundGoals) {
            return;
        }

        if (isAllSelected(foundGoals, selectedIds)) {
            return setSelected({});
        }

        setSelected(makeSelectedMapping(foundGoals));
    }, [foundGoals, selectedIds]);

    const onInput = useCallback(
        (event: FormEvent<HTMLInputElement>) => {
            const value = event.currentTarget.value;
            const newFoundGoals = value
                ? goals?.filter((goal) => goal.id.includes(value) || goal.name.includes(value))
                : goals;

            setFoundGoals(newFoundGoals);
        },
        [goals],
    );

    const onClickSaveButton = useCallback(() => {
        onSelect(selectedIds);
        onClose();
    }, [selectedIds, onSelect, onClose]);

    return (
        <Dialog
            maxWidth={MAX_WIDTH}
            onClose={onClose}
            dataTestId={DataTestId.CONTENT_PROMOTION_CHOOISING_GOALS_DIALOG}
        >
            <DialogTitle onClose={onClose} withBorderBottom={false} overflow="visible">
                <Typography variant="title3" weight="medium" mr={2}>
                    {t('Целевые действия, которые хотите получать на сайте')}
                </Typography>
            </DialogTitle>
            <DialogContent px={0} py={0}>
                <Box px={3} width="100%">
                    <Input variant="outline" placeholder={t('Поиск')} onInput={onInput} />
                </Box>
                {foundGoals?.length ? (
                    <ContentPromotionGoalsDialogContent
                        goals={foundGoals}
                        selected={selected}
                        isAllSelected={isAllSelected(foundGoals, selectedIds)}
                        onSelectAll={onSelectAll}
                        onSelect={onSelectRow}
                    />
                ) : (
                    <Typography variant="body" weight="medium" m="auto" py={2} px={3}>
                        {t('Ничего не найдено')}
                    </Typography>
                )}
            </DialogContent>
            <DialogFooter minHeight={FOOTER_HEIGHT}>
                <Button
                    width="100%"
                    mt={0}
                    size="l"
                    variant="filled"
                    color="primary"
                    disabled={!selectedIds.length}
                    onClick={onClickSaveButton}
                >
                    {t('Добавить')}
                </Button>
            </DialogFooter>
        </Dialog>
    );
};
